import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import GeneralInfoModal from '../components/GeneralInfoModal';
import RecordViewerModal from '../components/RecordViewerModal';
import EditServiceUserForm from '../components/EditServiceUserForm';
import ServiceUserGPModule from '../components/ServiceUserGPModule';
import ServiceUserPharmacyModule from '../components/ServiceUserPharmacyModule';
import ServiceUserSocialWorkerModule from '../components/ServiceUserSocialWorkerModule';
import ServiceUserPOAModule from '../components/ServiceUserPOAModule';
import DocumentCloudModal from '../components/DocumentCloudModal';
import ManageAllergiesModal from '../components/ManageAllergiesModal';
import MentalCapacityAssessmentModal from '../components/MentalCapacityAssessmentModal';
import WishesPreferencesModal from '../components/WishesPreferencesModal';
import EvidenceRegisterModal from '../components/EvidenceRegisterModal';
import CarePlansModal from '../components/CarePlansModal';
import RiskAssessmentsModal from '../components/RiskAssessmentsModal';
import MedicationEMARModal from '../components/MedicationEMARModal';
import SkinIntegrityModal from '../components/SkinIntegrityModal';
import HospitalAdmissionsModal from '../components/HospitalAdmissionsModal';
import IncidentFormsModal from '../components/IncidentFormsModal';
import MedicationAlertsModal from '../components/MedicationAlertsModal';
import OverdueReviewsModal from '../components/OverdueReviewsModal';
import JournalModal from '../components/JournalModal';
import ContactLogsModal from '../components/ContactLogsModal';
import VisitNotesModal from '../components/VisitNotesModal';
import { hasPermission } from '../config/permissions';

export default function ServiceUserDetailsPage() {
    const { serverId, serviceUserId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { userRole } = useAuth();
    const canEdit = hasPermission(userRole, 'CAN_EDIT_DATA');
    
    // Core State
    const [serviceUser, setServiceUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    // 1. Listen for Edit Signal
    useEffect(() => {
        if (location.state?.openEditModal) {
            setIsEditModalOpen(true);
            window.history.replaceState({}, document.title);
        }
    }, [location]);
    
    // Sub-collection Counts State
    const [counts, setCounts] = useState({
        carePlans: 0, riskAssessment: 0, mentalAssessment: 0, journal: 0,
        documents: 0, allergies: 0, skinIntegrity: 0, myMedications: 0,
        wishesPreferences: 0, incidentForms: 0, medicationAlerts: 0,
        hospitalAdmissions: 0, visitNotes: 0, contactLogs: 0
    });

    // Modals State
    const [showGeneralInfo, setShowGeneralInfo] = useState(false);
    const [showGP, setShowGP] = useState(false);
    const [showPharmacy, setShowPharmacy] = useState(false);
    const [showSocialWorker, setShowSocialWorker] = useState(false);
    const [showPOA, setShowPOA] = useState(false);
    const [showDocumentCloud, setShowDocumentCloud] = useState(false);
    const [showAllergies, setShowAllergies] = useState(false);
    const [showMCA, setShowMCA] = useState(false);
    const [showWishes, setShowWishes] = useState(false);
    const [showEvidence, setShowEvidence] = useState(false);
    const [showCarePlans, setShowCarePlans] = useState(false);
    const [showRiskAssessments, setShowRiskAssessments] = useState(false);
    const [showMedications, setShowMedications] = useState(false);
    const [showSkinIntegrity, setShowSkinIntegrity] = useState(false);
    const [showHospitalAdmissions, setShowHospitalAdmissions] = useState(false);
    
    const [showIncidentForms, setShowIncidentForms] = useState(false);
    const [showMedicationAlerts, setShowMedicationAlerts] = useState(false);
    const [showOverdueReviews, setShowOverdueReviews] = useState(false);
    const [showJournal, setShowJournal] = useState(false);
    const [showContactLogs, setShowContactLogs] = useState(false);
    const [showVisitNotes, setShowVisitNotes] = useState(false);
    
    const [viewerConfig, setViewerConfig] = useState({
        isOpen: false,
        collectionName: '',
        title: ''
    });

    useEffect(() => {
        async function fetchDashboardData() {
            setLoading(true);
            try {
                // 1. Fetch Service User base document
                // Path: servers/{serverId}/serviceUsers/{serviceUserId}
                const docRef = doc(db, 'servers', serverId, 'serviceUsers', serviceUserId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setServiceUser({ id: docSnap.id, ...docSnap.data() });
                } else {
                    console.error("No such service user!");
                    setLoading(false);
                    return;
                }

                // 2. Safely Fetch Counts for Sub-collections to populate dashboard badges
                // Helper to get count for a specific sub-collection path
                const getCount = async (colName) => {
                    try {
                        const colRef = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, colName);
                        const snapshot = await getCountFromServer(colRef);
                        return snapshot.data().count;
                    } catch (e) {
                        return 0; // Silently fail to 0 if permission issues or doesn't exist
                    }
                };

                // Run count queries concurrently for performance
                const results = await Promise.all([
                    getCount('carePlans'),
                    getCount('riskAssessment'),
                    getCount('mentalAssessment'),
                    getCount('journal'),
                    getCount('documents'),
                    getCount('allergies'),
                    getCount('skinIntegrity'),
                    getCount('myMedications'),
                    getCount('wishesPreferences'),
                    getCount('incidentForms'),
                    getCount('medicationAlerts'),
                    getCount('hospitalAdmissions'),
                    getCount('visitNotes'),
                    getCount('contactLogs')
                ]);

                setCounts({
                    carePlans: results[0],
                    riskAssessment: results[1],
                    mentalAssessment: results[2],
                    journal: results[3],
                    documents: results[4],
                    allergies: results[5],
                    skinIntegrity: results[6],
                    myMedications: results[7],
                    wishesPreferences: results[8],
                    incidentForms: results[9],
                    medicationAlerts: results[10],
                    hospitalAdmissions: results[11],
                    visitNotes: results[12],
                    contactLogs: results[13]
                });

            } catch (error) {
                console.error("Error fetching service user dashboard:", error);
            } finally {
                setLoading(false);
            }
        }
        
        if (serverId && serviceUserId) {
            fetchDashboardData();
        }
    }, [serverId, serviceUserId]);

    // --- RENDER HELPERS ---

    const ModuleTile = ({ title, subtitle, icon, onClick, badge }) => (
        <div 
            onClick={onClick}
            style={{
                backgroundColor: 'white', borderRadius: '12px', padding: '20px', 
                border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '16px',
                transition: 'all 0.2s ease', ':hover': { boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }
            }}
        >
            <div style={{ fontSize: '28px', backgroundColor: '#eff6ff', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                {icon}
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 'bold', color: '#111827' }}>{title}</h4>
                    {badge !== undefined && (
                        <span style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '12px', fontWeight: 'bold', padding: '2px 8px', borderRadius: '12px' }}>
                            {badge}
                        </span>
                    )}
                </div>
                <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: 1.4 }}>{subtitle}</p>
            </div>
        </div>
    );

    const Section = ({ title, children }) => (
        <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#374151', marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #e5e7eb' }}>
                {title}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                {children}
            </div>
        </div>
    );

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading Dashboard Modules...</div>;
    if (!serviceUser) return <div style={{ padding: 40, textAlign: 'center' }}>Service User not found.</div>;

    return (
        <div style={{ padding: '30px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
            {/* Header Profile Section */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '32px', backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <button onClick={() => navigate(-1)} style={{ marginRight: '24px', padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: 'pointer', fontWeight: 'bold', color: '#374151' }}>← Back</button>
                <img src={serviceUser.profilePicture || 'https://ui-avatars.com/api/?background=random&name=' + encodeURIComponent(serviceUser.fullName)} alt="Profile" style={{ width: '80px', height: '80px', borderRadius: '50%', marginRight: '24px', objectFit: 'cover', border: '3px solid #eff6ff' }} />
                <div>
                    <h1 style={{ margin: 0, fontSize: '26px', color: '#111827', fontWeight: 'bold' }}>{serviceUser.fullName}</h1>
                    <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: '14px' }}>
                        ID: <span style={{ fontWeight: '500', color: '#374151' }}>{serviceUser.serviceUserID || 'N/A'}</span> • Type: <span style={{ fontWeight: '500', color: '#374151' }}>{serviceUser.type || 'Service User'}</span>
                    </p>
                </div>
                {canEdit && (
                    <button 
                        onClick={() => setIsEditModalOpen(true)} 
                        style={{ marginLeft: 'auto', padding: '10px 20px', backgroundColor: '#0056b3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        Edit Profile
                    </button>
                )}
            </div>

            {/* DASHBOARD SECTIONS */}

            <Section title="Compliance Items">
                <ModuleTile 
                    icon="📋" title="General Information" subtitle="Open personal, contact, and billing information"
                    onClick={() => setShowGeneralInfo(true)}
                />
                <ModuleTile 
                    icon="⚠️" title="Manage Allergies" subtitle="View active allergies and intolerances" badge={counts.allergies}
                    onClick={() => setShowAllergies(true)}
                />
                <ModuleTile 
                    icon="🧠" title="Mental Capacity Assessment" subtitle="MCA records and documentation" badge={counts.mentalAssessment}
                    onClick={() => setShowMCA(true)}
                />
                <ModuleTile 
                    icon="🌟" title="Wishes & Preferences" subtitle="Personal choices and care preferences" badge={counts.wishesPreferences}
                    onClick={() => setShowWishes(true)}
                />
                <ModuleTile 
                    icon="✅" title="Evidence Register" subtitle="Compliance evidence tracking"
                    onClick={() => setShowEvidence(true)}
                />
            </Section>

            <Section title="Clinical Items">
                <ModuleTile 
                    icon="📝" title="Care Planning Centre" subtitle="Create and review active care plans" badge={counts.carePlans}
                    onClick={() => setShowCarePlans(true)}
                />
                <ModuleTile 
                    icon="🏥" title="Risk Assessments" subtitle="Moving, assisting, nutrition, falls" badge={counts.riskAssessment}
                    onClick={() => setShowRiskAssessments(true)}
                />
                <ModuleTile 
                    icon="💊" title="Medication (EMAR)" subtitle="Electronic Medication Administration Records" badge={counts.myMedications}
                    onClick={() => setShowMedications(true)}
                />
                <ModuleTile 
                    icon="🩺" title="Skin Integrity" subtitle="Wound care and pressure area monitoring" badge={counts.skinIntegrity}
                    onClick={() => setShowSkinIntegrity(true)}
                />
                <ModuleTile 
                    icon="🚑" title="Hospital Admissions" subtitle="Record and review admission and discharge data" badge={counts.hospitalAdmissions}
                    onClick={() => setShowHospitalAdmissions(true)}
                />
            </Section>

            <Section title="Linked Items">
                <ModuleTile 
                    icon="👨‍⚕️" title="General Physician" subtitle="GP details and practice info"
                    onClick={() => setShowGP(true)}
                />
                <ModuleTile 
                    icon="💊" title="Pharmacy" subtitle="Nominated pharmacy details"
                    onClick={() => setShowPharmacy(true)}
                />
                <ModuleTile 
                    icon="🤝" title="Social Worker" subtitle="Social care contact details"
                    onClick={() => setShowSocialWorker(true)}
                />
                <ModuleTile 
                    icon="⚖️" title="Power of Attorney" subtitle="Legal representatives"
                    onClick={() => setShowPOA(true)}
                />
                <ModuleTile 
                    icon="☁️" title="Document Cloud" subtitle="Scanned documents and uploads" badge={counts.documents}
                    onClick={() => setShowDocumentCloud(true)}
                />
            </Section>

            <Section title="Oversight Items">
                <ModuleTile 
                    icon="🚨" title="Incident Forms" subtitle="Review incidents and accidents" badge={counts.incidentForms}
                    onClick={() => setShowIncidentForms(true)}
                />
                <ModuleTile 
                    icon="🔔" title="Medication Alerts" subtitle="Missed or refused medication alerts" badge={counts.medicationAlerts}
                    onClick={() => setShowMedicationAlerts(true)}
                />
                <ModuleTile 
                    icon="📅" title="Overdue Reviews" subtitle="Care plans requiring updates" badge={0}
                    onClick={() => setShowOverdueReviews(true)}
                />
            </Section>

            <Section title="Activity Items">
                <ModuleTile 
                    icon="📓" title="Journal" subtitle="Daily notes, updates, and general journal" badge={counts.journal}
                    onClick={() => setShowJournal(true)}
                />
                <ModuleTile 
                    icon="📞" title="Contact Logs" subtitle="Communications with family and professionals" badge={counts.contactLogs}
                    onClick={() => setShowContactLogs(true)}
                />
                <ModuleTile 
                    icon="🏡" title="Visit Notes" subtitle="Carer logs from scheduled visits" badge={counts.visitNotes}
                    onClick={() => setShowVisitNotes(true)}
                />
            </Section>

            {/* MODALS */}
            
            {showGeneralInfo && (
                <GeneralInfoModal 
                    serviceUser={serviceUser} 
                    onClose={() => setShowGeneralInfo(false)} 
                />
            )}

            {showGP && (
                <ServiceUserGPModule 
                    serviceUser={serviceUser} 
                    onClose={() => setShowGP(false)} 
                />
            )}

            {showPharmacy && (
                <ServiceUserPharmacyModule 
                    serviceUser={serviceUser} 
                    onClose={() => setShowPharmacy(false)} 
                />
            )}

            {showSocialWorker && (
                <ServiceUserSocialWorkerModule 
                    serviceUser={serviceUser} 
                    onClose={() => setShowSocialWorker(false)} 
                />
            )}

            {showPOA && (
                <ServiceUserPOAModule 
                    serviceUser={serviceUser} 
                    onClose={() => setShowPOA(false)} 
                />
            )}

            {showDocumentCloud && (
                <DocumentCloudModal 
                    serverId={serverId}
                    serviceUserId={serviceUserId}
                    onClose={() => setShowDocumentCloud(false)} 
                />
            )}

            {showAllergies && (
                <ManageAllergiesModal 
                    serverId={serverId}
                    serviceUserId={serviceUserId}
                    onClose={() => setShowAllergies(false)} 
                />
            )}

            {showMCA && (
                <MentalCapacityAssessmentModal 
                    serverId={serverId}
                    serviceUserId={serviceUserId}
                    onClose={() => setShowMCA(false)} 
                />
            )}

            {showWishes && (
                <WishesPreferencesModal 
                    serverId={serverId}
                    serviceUserId={serviceUserId}
                    onClose={() => setShowWishes(false)} 
                />
            )}

            {showEvidence && (
                <EvidenceRegisterModal 
                    serverId={serverId}
                    serviceUserId={serviceUserId}
                    onClose={() => setShowEvidence(false)} 
                />
            )}

            {showCarePlans && <CarePlansModal serverId={serverId} serviceUserId={serviceUserId} onClose={() => setShowCarePlans(false)} />}
            {showRiskAssessments && <RiskAssessmentsModal serverId={serverId} serviceUserId={serviceUserId} onClose={() => setShowRiskAssessments(false)} />}
            {showMedications && <MedicationEMARModal serverId={serverId} serviceUserId={serviceUserId} onClose={() => setShowMedications(false)} />}
            {showSkinIntegrity && <SkinIntegrityModal serverId={serverId} serviceUserId={serviceUserId} onClose={() => setShowSkinIntegrity(false)} />}
            {showHospitalAdmissions && <HospitalAdmissionsModal serverId={serverId} serviceUserId={serviceUserId} onClose={() => setShowHospitalAdmissions(false)} />}
            
            {showIncidentForms && <IncidentFormsModal serverId={serverId} serviceUserId={serviceUserId} onClose={() => setShowIncidentForms(false)} />}
            {showMedicationAlerts && <MedicationAlertsModal serverId={serverId} serviceUserId={serviceUserId} onClose={() => setShowMedicationAlerts(false)} />}
            {showOverdueReviews && <OverdueReviewsModal serverId={serverId} serviceUserId={serviceUserId} onClose={() => setShowOverdueReviews(false)} />}
            {showJournal && <JournalModal serverId={serverId} serviceUserId={serviceUserId} onClose={() => setShowJournal(false)} />}
            {showContactLogs && <ContactLogsModal serverId={serverId} serviceUserId={serviceUserId} onClose={() => setShowContactLogs(false)} />}
            {showVisitNotes && <VisitNotesModal serverId={serverId} serviceUserId={serviceUserId} onClose={() => setShowVisitNotes(false)} />}

            {/* Generic Viewer Fallback */}
            {viewerConfig.isOpen && (
                <RecordViewerModal 
                    serverId={serverId}
                    serviceUserId={serviceUserId}
                    collectionName={viewerConfig.collectionName}
                    title={viewerConfig.title}
                    onClose={() => setViewerConfig({ isOpen: false, collectionName: '', title: '' })}
                />
            )}

            {isEditModalOpen && (
                <EditServiceUserForm
                    serviceUser={serviceUser}
                    closeModal={() => setIsEditModalOpen(false)}
                    onUpdate={() => window.location.reload()}
                    serverId={serverId}
                />
            )}
        </div>
    );
}